import Drawer from 'tio-ui/components/drawer';
import Button from 'tio-ui/components/button';
import ConfirmationDialog from 'tio-ui/components/confirmation-dialog';
import AlertBanner from 'tio-ui/components/alert-banner';
import { PhFilePdf } from 'ember-phosphor-icons';
import { Section, VStack } from 'tio-ui/components/layout';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { trackedFunction } from 'reactiveweb/function';
import Component from '@glimmer/component';
import pageTitle from 'ember-page-title/helpers/page-title';
import { limitsEvaluationDate } from '../../../utils/tuition-assistance/limits-evaluation-date.ts';
import type IntlService from 'ember-intl/services/intl';
import type FeaturesService from '../../../services/features.ts';
import type { TasApplicationExtension as TasApplicationModel } from 'tio-data/data/resources/tas-application.ext';
import type { CustomFieldSignature } from '../../../types/tuition-assistance.ts';
import { formatCustomFieldAnswer } from '../../../utils/tuition-assistance/custom-field-value.ts';
import { trackedObject } from '@ember/reactive/collections';
import { dropTask, restartableTask, task, timeout } from 'ember-concurrency';
import { modifier } from 'ember-modifier';
import type { Store } from '@warp-drive/core';
import type { TasCourseExtension as TasCourseModel } from 'tio-data/data/resources/tas-course.ext';
import type Inputmask from 'inputmask';
import type SessionContextService from '../../../services/session-context.ts';
import type { NotificationsService } from 'tio-ui/components/notifications';
import type TuitionAssistanceService from '../../../services/tuition-assistance.ts';
import type Owner from '@ember/owner';
import {
  getSingleValueForTasField,
  setSingleValueForTasField,
  setTrackedTasFieldValue,
  mergeCustomFieldValues,
  getSortedOptionsByFieldName,
} from '../../../utils/tuition-assistance/fields.ts';
import {
  approvedTotalFieldForState,
  canSyncApprovedTotalFromRequest,
} from '../../../utils/tuition-assistance/approved-total-sync.ts';
import {
  reloadRecord,
  saveRecord,
  saveExistingRecordWithChangelog,
} from 'tio-data/requests/save-record';
import { periodsOfEligibilityRequest } from 'tio-data/requests/tas-participant-actions';
import {
  createPdfAssetRequest,
  updateApprovedTotalRequest,
} from 'tio-data/requests/tas-application-actions';
import type { PeriodOfEligibility } from 'tio-data/types/tuition-assistance';
import type { EligibilityStatus } from 'tio-data/data/resources/eligibility-status.type';
import * as v from 'valibot';
import type { ValidationErrorSignature } from '../../tuition-assistance/forms/courses/grade-select-options.ts';
import type { LimitsStatusDataSignature } from '../../../services/tuition-assistance.ts';
import type RouterService from '@ember/routing/router-service';

type AiRecommendationResponse = {
  recommendation_summary?: {
    status?: string;
    calculation_details?: unknown;
  };
};

type ProgramTemplateFields =
  Record<string, { visible?: boolean; label?: string; required?: boolean }> | undefined;

import ApprovalFooter from './approval-footer.gts';
import type { ReferenceDrawer } from './reference-controls.gts';
import type TasApprovalLayoutService from '../../../services/tas-approval-layout.ts';
import AiAnalysisDrawer from './drawers/ai-analysis.gts';
import CommentsDrawer from './drawers/comments.gts';
import CoursesDrawer from './drawers/courses.gts';
import PartnerInfoDrawer from './drawers/partner-info.gts';
import FinancialDetailsDrawer from './drawers/financial-details.gts';
import AttachmentsDrawer from './drawers/attachments.gts';
import AttachmentsAnalysisView from './drawers/ai-analysis/attachments-analysis-view.gts';
import TimelineDrawer from './drawers/timeline.gts';
import ApprovalQueueStatusDrawer from './drawers/approval-queue-status.gts';
import FinancialDetailsAdminSummary from './financial-details-admin-summary.gts';
import ApprovalDecisionBanner from './approval-decision-banner.gts';
import HeldFundsModal from './held-funds-modal.gts';
import {
  heldFundsRequest,
  parseHeldFunds,
  type HeldAssistance,
  type HeldFundsWindow,
} from 'tio-data/requests/held-funds';
import { heldFundsDate, heldFundsTotals } from '../../../utils/tuition-assistance/held-funds.ts';
import AddScholarshipsDrawer from '../add-scholarships-drawer.gts';
import RecentCommentPreview from './recent-comment-preview.gts';
import ApplicationDetailsSection from './application-details-section.gts';
import ExpensesSection from './expenses-section.gts';
import RepaymentWarningBanner from './repayment-warning-banner.gts';

const classificationOptions = [
  { label: 'Final Approval Document', key: 'COMPLETION_DOCUMENTS' },
  { label: 'Approval Document', key: 'COURSE_APPROVAL_DOCUMENTS' },
  { label: 'Misc Approval Document (Admin)', key: 'APPLICATION_LIFECYCLE_DOCUMENTS' },
];

interface ApprovalPageSignature {
  Element: HTMLElement;
  Args: {
    model: TasApplicationModel;
    /** Query param to enable AI analysis in production (tio-admin only) */
    ai?: boolean;
    /** Admin-only read-only mode for applications outside the current user's approval queue. */
    isReadonlyView?: boolean;
    /** Optional callback to open the CourseCash drawer (admin-only) */
    onCourseCashOpen?: () => void;
    /** Whether the caller's CourseCash transactions are showing; they share the reference drawer. */
    isCourseCashOpen?: boolean;
    /** Closes the caller's CourseCash transactions when the approver closes or switches drawers. */
    onCourseCashClose?: () => void;
    /**
     * Rendered inside another screen, such as the TA audit pane: drawers and dialogs overlay
     * the page as before, and the approver's saved layout is not applied.
     */
    embedded?: boolean;
  };
  Blocks: {
    header: [];
    alerts?: [];
    /** CourseCash transactions, shown in the reference drawer while `@isCourseCashOpen`. */
    courseCash?: [];
  };
}

// Inputmask exposes userOptions at runtime but omits it from @types/inputmask.
type InputmaskTarget = HTMLInputElement & {
  inputmask?: Inputmask.Instance & { userOptions?: { alias?: string } };
};

export default class TasApprovalPage extends Component<ApprovalPageSignature> {
  @service declare sessionContext: SessionContextService;
  @service declare intl: IntlService;
  @service declare store: Store;
  @service declare tuitionAssistance: TuitionAssistanceService;
  @service declare features: FeaturesService;
  @service declare notifications: NotificationsService;
  @service declare router: RouterService;
  @service declare tasApprovalLayout: TasApprovalLayoutService;

  @tracked selectedDrawerType: string | null = null;
  @tracked isDrawerOpen = false;
  @tracked isDecisionFormOpen = false;
  @tracked referenceHost: HTMLElement | undefined;
  @tracked selectedCourseIndex = 0;
  @tracked showUploadSection = false;
  @tracked showGeneratePdfConfirm = false;
  @tracked selectedClassification = '';
  @tracked uploadClassificationField = '';

  @tracked isScholarshipDrawerOpen = false;

  @tracked editingCourseExpensesIndex: number | null = null;
  @tracked courseExpenseFieldsCopy: TasCourseModel['fields'] | null = null;
  @tracked expenseChangelog = '';
  /** Requested total as of entering expense edit mode — see canSyncApprovedTotalFromRequest */
  @tracked requestedTotalBeforeExpenseEdit: number | null = null;

  @tracked editingCourseDetailsIndex: number | null = null;
  @tracked courseDetailFieldsCopy: TasCourseModel['fields'] | null = null;
  @tracked courseCustomFieldsCopy: CustomFieldSignature[] | null = null;
  @tracked detailChangelog = '';

  // The footer reads edit state, and the scholarship drawer updates applicationFieldsCopy.
  @tracked isEditingApplicationDetails = false;
  @tracked applicationFieldsCopy: TasApplicationModel['fields'] | null = null;

  @tracked editableApplicationTotal = 0;
  @tracked editableCourseCashTotal = 0;

  @tracked commentsInitialTab: 'approver' | 'employee' = 'approver';

  @tracked private fetchedLimitsData: LimitsStatusDataSignature | undefined = undefined;
  // Withhold unconfirmed balances after a refresh fails.
  @tracked private limitsLoadFailed = false;

  @tracked heldFundsRows: HeldAssistance[] = [];
  // What the rows were read for; the dialog keeps it through a failed balance re-read.
  @tracked heldFundsLoadedFor: { applicationId: string; window: HeldFundsWindow } | null = null;
  @tracked isHeldFundsOpen = false;
  // Remaining benefit the ledger should reach once every saved reduction has landed.
  #expectedAvailable: number | null = null;

  @tracked eligibilityData: EligibilityStatus | undefined = undefined;
  @tracked eligibilityLoading = false;

  periodsOfEligibilityData = trackedFunction(this, async () => {
    try {
      const programInstance = this.args.model.tasProgramInstance;
      if (!programInstance?.employee?.tasParticipant?.id) {
        return [];
      }

      const response = await this.store.request(
        periodsOfEligibilityRequest(programInstance.employee.tasParticipant.id)
      );
      const periods = response.content as PeriodOfEligibility;
      return periods[programInstance.tasProgramTemplate.code] as [string, string][];
    } catch (e) {
      console.warn('Error fetching eligibility periods:', e);
      return [];
    }
  });

  constructor(owner: Owner, args: ApprovalPageSignature['Args']) {
    super(owner, args);

    const calculateTotalByClaimsFinancials =
      args.model.tasProgramInstance?.tasProgramTemplate?.calculateTotalByClaimsFinancials;

    this.editableApplicationTotal = calculateTotalByClaimsFinancials
      ? args.model.recalculateClaimsFinancialsAfterAdjacentUpdate().CF_REIMBURSEMENT_TOTAL
      : ((getSingleValueForTasField(
          args.model.fieldForEditableApprovedTotal,
          args.model.fields
        ) as number) ??
        (args.model.approvedTotal as number) ??
        (args.model.requestedTotal as number));

    this.editableCourseCashTotal = Number(args.model.cardPreApprovedTotal);

    void this.loadLimitsData().then(() => {
      void this.loadHeldFunds.perform();
    });

    if (this.features.isAdminApp) {
      void this.loadEligibilityData();
    }

    if (!args.embedded) {
      void this.tasApprovalLayout.load.perform();
    }
  }

  /** Fetches scheme-level limits for the employee and application to display the remaining balance. */
  loadLimitsData = async () => {
    const employee = this.args.model.tasProgramInstance?.employee;
    if (!employee) {
      console.warn('Cannot load limits data: employee not found on application');
      return;
    }

    try {
      this.fetchedLimitsData = await this.tuitionAssistance.getV2LimitsStatus(
        employee,
        this.args.model,
        limitsEvaluationDate(this.args.model)
      );
      this.limitsLoadFailed = false;
    } catch (error) {
      this.limitsLoadFailed = true;
      console.error('Error loading V2 limits data:', {
        applicationId: this.args.model.id,
        employeeId: employee.id,
        error,
      });
    }
  };

  /**
   * Held-funds reallocation is for TIO admins and TA superusers on dollar limits; the API
   * enforces the same roles, so this only spares everyone else a refused request.
   */
  get canReallocateHeldFunds() {
    const user = this.sessionContext.user;
    return (
      this.features.isAdminApp &&
      this.features.isEnabled('tasReallocateHeldFunds') &&
      (!!user?.isTioAdmin ||
        !!user?.roles?.some((role) => role.relationshipType === 'TAS.Admin.SUPERUSER'))
    );
  }

  // The status call's resolved date, so both endpoints judge releases in the same window.
  get heldFundsWindow(): HeldFundsWindow | null {
    const date = heldFundsDate(this.limitsData?.as_of_date);
    if (!date || this.limitsData?.limit_capacity === 'TAS.LimitCapacity.CREDITS') return null;
    return { date, product: 'TA' };
  }

  get heldFundsTarget() {
    const window = this.heldFundsWindow;
    const applicationId = this.args.model.id;
    return window && applicationId ? { applicationId, window } : null;
  }

  get heldFundsSummary() {
    if (!this.heldFundsLoadedFor) return undefined;
    return { ...heldFundsTotals(this.heldFundsRows), listed: this.heldFundsRows.length };
  }

  /**
   * Reads the holds on the employee's other applications for this application's benefit
   * window. A failed first read leaves no list, which hides the Benefit tile's link.
   */
  loadHeldFunds = restartableTask(async () => {
    const target = this.heldFundsTarget;
    if (!this.canReallocateHeldFunds || !target) return;
    const { applicationId, window } = target;
    try {
      const { content } = await this.store.request(heldFundsRequest(applicationId, window));
      this.heldFundsRows = parseHeldFunds(content as Parameters<typeof parseHeldFunds>[0]).rows;
      this.heldFundsLoadedFor = target;
    } catch (error) {
      // A failed refresh keeps the last good list, so an open dialog is not torn down under the approver.
      if (!this.heldFundsLoadedFor) this.heldFundsRows = [];
      console.error('Error loading held funds:', { applicationId, error });
    }
  });

  openHeldFunds = () => {
    this.isHeldFundsOpen = true;
    void this.loadHeldFunds.perform();
  };

  reloadHeldFunds = () => {
    void this.loadHeldFunds.perform();
  };

  closeHeldFunds = () => {
    this.isHeldFundsOpen = false;
  };

  onHeldFundsReduced = (updated: HeldAssistance, freed: number) => {
    this.heldFundsRows = this.heldFundsRows.map((row) =>
      row.assistanceId === updated.assistanceId ? updated : row
    );
    const available = this.limitsData?.available;
    if (typeof available === 'number' && freed > 0) {
      this.#expectedAvailable = (this.#expectedAvailable ?? available) + freed;
      void this.awaitLedger.perform();
    }
  };

  /**
   * The ledger is written asynchronously, so re-reads the balance every 3s, for up to 30s,
   * until it reflects every saved release; then refreshes the held-funds list against it.
   */
  awaitLedger = restartableTask(async () => {
    for (let attempt = 0; attempt < 10; attempt++) {
      await timeout(3000);
      await this.loadLimitsData();
      const available = this.limitsData?.available;
      if (typeof available === 'number' && available >= (this.#expectedAvailable ?? 0)) break;
    }
    this.#expectedAvailable = null;
    await this.loadHeldFunds.perform();
  });

  get limitsData(): LimitsStatusDataSignature | undefined {
    return this.limitsLoadFailed ? undefined : this.fetchedLimitsData;
  }

  // Separates failed reads from successful reads with no configured limit.
  get isLimitsUnavailable() {
    return this.limitsLoadFailed;
  }

  /** Fetches participant, employment, and eligibility status for the admin decision banner. */
  loadEligibilityData = async () => {
    const applicationId = this.args.model.id;
    if (!applicationId) {
      return;
    }

    this.eligibilityLoading = true;
    try {
      this.eligibilityData = await this.tuitionAssistance.getEligibilityStatus(applicationId);
    } catch (error) {
      console.error('Error loading eligibility status:', {
        applicationId,
        error,
      });
    } finally {
      this.eligibilityLoading = false;
    }
  };

  /** Full-page approvals dock their drawers beside the page; an embedded page keeps the overlay. */
  get docksReferences() {
    return this.args.embedded !== true;
  }

  get referenceControls() {
    return this.docksReferences ? this.tasApprovalLayout.referenceControls : 'bottom';
  }

  get isReferenceOpen() {
    return this.isDrawerOpen || this.args.isCourseCashOpen === true;
  }

  get activeReference() {
    return this.args.isCourseCashOpen ? 'course-cash' : this.selectedDrawerType;
  }

  get hasCourseCash() {
    return (
      this.args.model.paymentMethod === 'TAS.AssistanceManner.COURSE_CASH' &&
      Boolean(this.args.onCourseCashOpen)
    );
  }

  /** Unsaved edits an approve form opened now would not see. */
  get hasUnsavedEdits() {
    return (
      this.isEditingApplicationDetails ||
      this.editingCourseExpensesIndex !== null ||
      this.editingCourseDetailsIndex !== null
    );
  }

  /** Approve and Request Change snapshot the application when they open, so edits wait until they close. */
  get isEditingLocked() {
    return this.isReadonlyView || this.isDecisionFormOpen;
  }

  openDrawer = (type: string) => {
    if (this.args.isCourseCashOpen) this.args.onCourseCashClose?.();
    if (type !== 'course-details') this.#cancelCourseEditing();
    this.selectedDrawerType = type;
    this.isDrawerOpen = true;
  };

  /** Opens a reference drawer, or closes it when it is already the open one. */
  toggleReference = (drawer: ReferenceDrawer) => {
    if (this.isReferenceOpen && this.activeReference === drawer) {
      this.closeDrawer();
    } else if (drawer === 'course-cash') {
      this.#cancelCourseEditing();
      this.isDrawerOpen = false;
      this.selectedDrawerType = null;
      this.args.onCourseCashOpen?.();
    } else {
      this.openDrawer(drawer);
    }
  };

  openCommentsDrawer = (tab: 'approver' | 'employee' = 'approver') => {
    this.commentsInitialTab = tab;
    this.openDrawer('comments');
  };

  closeDrawer = () => {
    if (this.args.isCourseCashOpen) this.args.onCourseCashClose?.();
    this.isDrawerOpen = false;
    this.selectedDrawerType = null;
    this.#cancelCourseEditing();
  };

  #cancelCourseEditing() {
    if (this.editingCourseExpensesIndex !== null) {
      this.editingCourseExpensesIndex = null;
      this.courseExpenseFieldsCopy = null;
      this.expenseChangelog = '';
    }

    if (this.editingCourseDetailsIndex !== null) {
      this.editingCourseDetailsIndex = null;
      this.courseDetailFieldsCopy = null;
      this.detailChangelog = '';
    }
  }

  setDecisionFormOpen = (isOpen: boolean) => {
    this.isDecisionFormOpen = isOpen;
  };

  moveReferenceControls = task(async () => {
    try {
      await this.tasApprovalLayout.setReferenceControls(
        this.referenceControls === 'right' ? 'bottom' : 'right'
      );
    } catch {
      this.notifications.add(this.intl.t('common.tas.approval.reference_controls.save_failed'), {
        appearance: 'error',
      });
    }
  });

  captureReferenceHost = modifier((element: HTMLElement) => {
    this.referenceHost = element;
  });

  /** Publishes the action bar's height so a docked drawer can stop above it. */
  observeActionBar = modifier((actionBar: HTMLElement) => {
    const layout = actionBar.closest<HTMLElement>('.tas-approval-layout');
    if (!layout) return;
    const publish = () =>
      layout.style.setProperty('--tas-approval-actions-height', `${actionBar.offsetHeight}px`);
    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(actionBar);
    return () => {
      observer.disconnect();
      layout.style.removeProperty('--tas-approval-actions-height');
    };
  });

  courseTabDidChange = (index: number) => {
    this.selectedCourseIndex = index;
  };

  openCourseDrawer = (courseIndex: number) => {
    this.selectedCourseIndex = courseIndex;
    this.openDrawer('course-details');
  };

  toggleUploadSection = () => {
    this.showUploadSection = !this.showUploadSection;
    if (this.showUploadSection) {
      this.selectedClassification = '';
      this.uploadClassificationField = '';
    }
  };

  hideUploadSection = () => {
    this.showUploadSection = false;
    this.selectedClassification = '';
    this.uploadClassificationField = '';
  };

  get canGeneratePdfAsset() {
    // TasPdfAssetService accepts voucher templates and allowlisted companies only.
    return (
      this.consumingApp === 'tio-admin' &&
      !!this.sessionContext.user.isTioAdmin &&
      !!this.args.model.tasProgramInstance?.tasProgramTemplate?.isVoucherProgram
    );
  }

  // The generated voucher uses VOUCHER_FILENAME, allowing the dialog to name it.
  get voucherDocumentName() {
    return (
      this.args.model.tasProgramInstance?.tasProgramTemplate?.fields?.['VOUCHER_FILENAME']
        ?.values?.[0] ?? ''
    );
  }

  // Matching lifecycle PDFs signal that generation may duplicate an existing document.
  get existingLifecycleDocumentsCount() {
    return this.allAttachments.filter(
      (asset) =>
        asset.classification === 'APPLICATION_LIFECYCLE_DOCUMENTS' &&
        asset.attachmentDeletedAt === null
    ).length;
  }

  openGeneratePdfConfirm = () => {
    this.showGeneratePdfConfirm = true;
  };

  closeGeneratePdfConfirm = () => {
    this.showGeneratePdfConfirm = false;
  };

  // CreatePdfAssetPolicy rejects non-allowlisted companies with 400.
  generatePdfAsset = dropTask(async () => {
    const applicationId = this.args.model.id;
    if (!applicationId) {
      this.notifications.add(
        this.intl.t('common.tas.approval.drawer.attachments.generate_pdf_failed'),
        { appearance: 'error' }
      );
      this.showGeneratePdfConfirm = false;
      return;
    }

    try {
      await this.store.request(createPdfAssetRequest(applicationId));
      this.notifications.add(
        this.intl.t('common.tas.approval.drawer.attachments.generate_pdf_started'),
        { appearance: 'success' }
      );
    } catch {
      this.notifications.add(
        this.intl.t('common.tas.approval.drawer.attachments.generate_pdf_failed'),
        { appearance: 'error' }
      );
    } finally {
      this.showGeneratePdfConfirm = false;
    }
  });

  attachmentClassificationDidChange = (classification: string) => {
    this.selectedClassification = classification;
    this.uploadClassificationField = classification || 'APPLICATION_LIFECYCLE_DOCUMENTS';
  };

  // The drawer stays at page level so closing it can synchronize applicationFieldsCopy.
  openScholarshipDrawer = () => {
    if (this.isReadonlyView) return;

    this.isScholarshipDrawerOpen = true;
  };

  closeScholarshipDrawer = () => {
    this.isScholarshipDrawerOpen = false;
    this.syncScholarshipsFromApplication();
  };

  syncScholarshipsFromApplication() {
    if (this.applicationFieldsCopy && this.args.model.fields['SCHOLARSHIPS_RECEIVED']) {
      this.applicationFieldsCopy['SCHOLARSHIPS_RECEIVED'] =
        this.args.model.fields['SCHOLARSHIPS_RECEIVED'];
    }
  }

  toggleCourseExpenseEdit = () => {
    const isCurrentlyEditing = this.editingCourseExpensesIndex === this.selectedCourseIndex;

    if (isCurrentlyEditing) {
      this.editingCourseExpensesIndex = null;
      this.courseExpenseFieldsCopy = null;
      this.expenseChangelog = '';
      this.requestedTotalBeforeExpenseEdit = null;
    } else {
      const course = this.selectedCourse;
      if (!course) return;

      this.courseExpenseFieldsCopy = trackedObject(course.fields);
      this.expenseChangelog = '';
      // Capture before editing to distinguish request-tracking totals from manually approved amounts.
      this.requestedTotalBeforeExpenseEdit = this.requestedTotalForApproval;
      this.editingCourseExpensesIndex = this.selectedCourseIndex;
    }
  };

  updateCourseExpenseField = (fieldName: string, event: Event) => {
    if (!this.courseExpenseFieldsCopy) return;

    let valueToSet: number;
    const target = event.target as InputmaskTarget;

    if (target?.inputmask?.userOptions?.alias === 'currency') {
      // Inputmask returns dollars; store minor units.
      valueToSet = Number(target.inputmask?.unmaskedvalue()) * 100;
    } else {
      // Unmasked fallback input is also in dollars.
      const value = target?.value ?? '';
      valueToSet = value ? parseFloat(value.replace(/[^0-9.-]/g, '')) * 100 : 0;
    }

    setSingleValueForTasField(
      fieldName as keyof TasCourseModel['fields'],
      valueToSet,
      this.courseExpenseFieldsCopy
    );
  };

  updateExpenseChangelog = (value: string) => {
    this.expenseChangelog = value;
  };

  saveCourseExpenses = dropTask(async () => {
    const course = this.selectedCourse;
    if (!course || !this.courseExpenseFieldsCopy) {
      return;
    }

    if (!this.isExpenseFormValid) {
      return;
    }

    try {
      course.fields = this.courseExpenseFieldsCopy;

      await saveExistingRecordWithChangelog(this.store, course, this.expenseChangelog);

      const application = this.args.model;

      if (
        course.tasApplication?.tasProgramInstance?.tasProgramTemplate?.isDirectPayProgram &&
        course.tasApplication.paymentMethod === 'TAS.AssistanceManner.COURSE_CASH'
      ) {
        this.setSubtotalsForDirectPay();
        await saveExistingRecordWithChangelog(this.store, application, this.expenseChangelog);
      }

      if (application.tasProgramInstance?.tasProgramTemplate?.calculateTotalByClaimsFinancials) {
        const calculatorResults = application.recalculateClaimsFinancialsAfterAdjacentUpdate();

        if (application.approvedTotal !== calculatorResults.CF_REIMBURSEMENT_TOTAL) {
          await this.updateApprovedTotal(calculatorResults.CF_REIMBURSEMENT_TOTAL);
        }
      }

      // Expenses are already saved; a totals-sync failure must not leave the drawer pretending the edit is unsaved.
      try {
        await this.#syncApprovedTotalWithCourseExpenses();
      } catch (error) {
        console.error('Error syncing approved total with updated course expenses:', error);
        this.notifications.add(this.intl.t('common.tas.approval.approved_total_sync_failed'), {
          appearance: 'error',
        });
      }

      this.tuitionAssistance.refreshActiveLimitsSummaryComponents();

      this.editingCourseExpensesIndex = null;
      this.courseExpenseFieldsCopy = null;
      this.expenseChangelog = '';
      this.requestedTotalBeforeExpenseEdit = null;

      this.courseExpensesFor(course);
    } catch (error) {
      console.error('Error saving course expenses:', error);
      // TODO: Show error notification to user
    }
  });

  /**
   * Sync expense edits only while approval tracks the request, preserving deliberate overrides.
   * Reload server state with tas-assistances so ownership and ledger guards do not use stale or
   * empty data.
   * A concurrent decision during this round-trip still needs an API conditional update to avoid
   * overwriting it.
   */
  #syncApprovedTotalWithCourseExpenses = async () => {
    const application = this.args.model;
    const requestedTotalBeforeEdit = this.requestedTotalBeforeExpenseEdit;

    if (!canSyncApprovedTotalFromRequest(application, requestedTotalBeforeEdit)) {
      return;
    }

    await reloadRecord(this.store, application, { include: 'tas-assistances' });

    if (!canSyncApprovedTotalFromRequest(application, requestedTotalBeforeEdit)) {
      return;
    }

    const requestedTotal = this.requestedTotalForApproval;

    if (application.approvedTotal === requestedTotal) {
      return;
    }

    await this.updateApprovedTotal(
      requestedTotal,
      this.expenseChangelog || 'Course expenses updated'
    );
  };

  /**
   * Requested total already includes the reimbursement percentage; GPA programs additionally apply
   * their tier cap.
   */
  get requestedTotalForApproval() {
    const application = this.args.model;
    const requestedTotal = application.requestedTotal;

    if (!application.requiresGPA) {
      return requestedTotal;
    }

    const gpa = Number(getSingleValueForTasField('GRADE_POINT_AVERAGE', application.fields) || 0);

    return Math.min(this.tuitionAssistance.setTotalBasedOnGPA(gpa), requestedTotal);
  }

  updateApprovedTotal = async (
    approvedTotal: number,
    reason: string = 'Course expenses updated',
    additionalFieldsToUpdate?: Record<string, unknown>
  ) => {
    const additionalFieldValues: Record<string, unknown> = {
      ...additionalFieldsToUpdate,
    };

    const calculateTotalByClaimsFinancials =
      this.args.model.tasProgramInstance?.tasProgramTemplate?.calculateTotalByClaimsFinancials;

    if (calculateTotalByClaimsFinancials) {
      const claims = this.args.model.recalculateClaimsFinancialsAfterAdjacentUpdate();
      additionalFieldValues['CLAIMS_FINANCIALS'] = claims;
    }

    // Let updateApprovedTotal save Course Cash totals so approved_total_log retains the correct prior value.

    await this.store.request(
      updateApprovedTotalRequest(
        this.args.model.id as string,
        approvedTotal,
        reason,
        additionalFieldValues
      )
    );
    await reloadRecord(this.store, this.args.model, { include: 'tas-assistances' });
    const updatedApplication = this.args.model;

    const fieldToCheck = approvedTotalFieldForState(this.args.model.state);
    const actualUpdatedValue = getSingleValueForTasField(
      fieldToCheck,
      updatedApplication.fields
    ) as number;

    if (actualUpdatedValue !== approvedTotal) {
      throw new Error(
        `Unable to update approved total. expected=${approvedTotal} actual=${actualUpdatedValue} (checked field: ${fieldToCheck})`
      );
    }

    this.editableApplicationTotal = updatedApplication.approvedTotal as number;
    // courseCashTotal is absent from the typed model; this optional read preserves an undefined value.
    this.editableCourseCashTotal = (updatedApplication as { courseCashTotal?: number })
      .courseCashTotal as number;
    this.tuitionAssistance.refreshActiveLimitsSummaryComponents();
  };

  setApplicationTotal = (value: number) => {
    this.editableApplicationTotal = Math.round(value * 100);
  };

  setCourseCashTotal = (value: number) => {
    this.editableCourseCashTotal = Math.round(value * 100);
  };

  setIsEditingApplicationDetails = (value: boolean) => {
    this.isEditingApplicationDetails = value;
  };

  setApplicationFieldsCopy = (fields: TasApplicationModel['fields'] | null) => {
    this.applicationFieldsCopy = fields;
  };

  // Accepts raw cents; setApplicationTotal instead accepts dollars.
  setEditableApplicationTotal = (value: number) => {
    this.editableApplicationTotal = value;
  };

  setCourseCashApprovedTotal = async (amount: number) => {
    const paymentMethodIsCourseCash =
      this.args.model.paymentMethod === 'TAS.AssistanceManner.COURSE_CASH';
    if (!paymentMethodIsCourseCash && !amount) return;

    setTrackedTasFieldValue(this.args.model, 'CARD_FINAL_APPROVED_TOTAL', amount);
    await saveRecord(this.store, this.args.model);
  };

  didUpdateClaimsFinancials = (claimsFinancials: { CF_REIMBURSEMENT_TOTAL: number }) => {
    // Reassign fields so WarpDrive includes them in the PATCH; in-place mutations are dropped.
    setTrackedTasFieldValue(this.args.model, 'CLAIMS_FINANCIALS', claimsFinancials);
    this.editableApplicationTotal = claimsFinancials.CF_REIMBURSEMENT_TOTAL;
  };

  saveUpdatedClaimsFinancialsWithApprovedTotal = async (
    claimsFinancials: { CF_REIMBURSEMENT_TOTAL: number },
    reason: string
  ) => {
    this.editableApplicationTotal = claimsFinancials.CF_REIMBURSEMENT_TOTAL;
    this.didUpdateClaimsFinancials(claimsFinancials);
    await this.updateApprovedTotal(this.editableApplicationTotal, reason);
  };

  updateInvoiceDate = (date: string) => {
    setTrackedTasFieldValue(this.args.model, 'INVOICE_DATE', date);
  };

  setSubtotalsForDirectPay() {
    const { requestedCourseCashSubtotalLessScholarships, requestedNonCourseCashSubtotal } =
      this.args.model;

    const typeClassification = this.args.model.typeClassification || '';
    const isTypeTwoProgram = typeClassification === 'TAS.ProgramType.2';

    const courseCashTotalField = isTypeTwoProgram
      ? 'CARD_FINAL_APPROVED_TOTAL'
      : 'CARD_PRE_APPROVED_TOTAL';
    const nonCourseCashTotalField = isTypeTwoProgram
      ? 'FINAL_APPROVED_TOTAL'
      : 'PRE_APPROVED_TOTAL';

    setTrackedTasFieldValue(
      this.args.model,
      courseCashTotalField,
      requestedCourseCashSubtotalLessScholarships
    );
    setTrackedTasFieldValue(
      this.args.model,
      nonCourseCashTotalField,
      requestedNonCourseCashSubtotal
    );
  }

  get isAiAnalysis() {
    return this.selectedDrawerType === 'ai-analysis';
  }

  get isComments() {
    return this.selectedDrawerType === 'comments';
  }

  get isEmployerInfo() {
    return this.selectedDrawerType === 'employer-info';
  }

  get isFinancialDetails() {
    return this.selectedDrawerType === 'financial-details';
  }

  get isAttachments() {
    return this.selectedDrawerType === 'attachments';
  }

  get isTimeline() {
    return this.selectedDrawerType === 'timeline';
  }

  get isCourseDetails() {
    return this.selectedDrawerType === 'course-details';
  }

  get isReassignApprover() {
    return this.selectedDrawerType === 'reassign-approver';
  }

  get consumingApp(): string {
    return this.features.isAdminApp
      ? 'tio-admin'
      : this.features.isEmployeeApp
        ? 'tio-employee'
        : this.features.isAgentApp
          ? 'tio-agent'
          : 'unknown';
  }

  /** Admins open the TAS builder; employer and partner portals retain their read-out. Returns undefined without a template ID. */
  get programTemplateRoute(): string | undefined {
    const programTemplate = this.args.model.tasProgramInstance?.tasProgramTemplate;
    if (!programTemplate?.id) return undefined;

    return this.consumingApp === 'tio-admin'
      ? 'authenticated.tas.program-builder'
      : 'authenticated.admin.tuition-assistance.program-templates.show';
  }

  get selectedCourse() {
    const courses = this.args.model.activeCourses || [];
    return courses[this.selectedCourseIndex];
  }

  get currentDrawerComponent() {
    return null; // We'll use conditional rendering instead
  }

  get approverInstructionsItems() {
    const instructions =
      this.args.model.tasProgramInstance?.tasProgramTemplate?.programApproverInstructions || [];
    return instructions.map((instruction) => ({
      heading: instruction.heading || `Instruction ${instruction.ordinal}`,
      body: instruction.body,
      ordinal: instruction.ordinal,
    }));
  }

  get employeeFaqsItems() {
    const faqs = this.args.model.tasProgramInstance?.tasProgramTemplate?.programFaqs || [];
    return faqs.map((faq) => ({
      heading: faq.heading || faq.question || 'FAQ',
      body: faq.body || faq.answer || '',
      ordinal: faq.ordinal || 0,
    }));
  }

  get approvalStatus() {
    const state = this.args.model.state;
    if (state?.includes('PENDING_COURSES_APPROVAL')) {
      return 'Pending Courses Approval';
    } else if (state?.includes('PENDING_EVIDENCE_APPROVAL')) {
      return 'Pending Evidence Approval';
    } else if (state?.includes('APPROVED')) {
      return 'Approved';
    } else if (state?.includes('REJECTED')) {
      return 'Rejected';
    }
    return 'Pending Review';
  }

  get isPostPaymentReview() {
    return (
      this.args.model.state === 'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL' &&
      (this.args.model.amounts?.paid ?? 0) > 0
    );
  }

  get applicationNumber() {
    return this.args.model.id || 'Unknown';
  }

  get employeeName() {
    const employee = this.args.model.tasProgramInstance?.employee;
    const person = employee?.person;
    if (person?.firstName && person?.lastName) {
      return `${person.firstName} ${person.lastName}`;
    }
    return person?.fullName || 'Unknown';
  }

  get payrollId() {
    return this.args.model.tasProgramInstance?.employee?.payrollId || 'Unknown';
  }

  get remainingBalance() {
    const schemeAvailable = this.limitsData?.details?.scheme?.available;

    if (schemeAvailable !== undefined && schemeAvailable !== null) {
      return `$${(schemeAvailable / 100).toFixed(2)}`;
    }

    const balance = this.args.model.courseCashAvailableBalance;
    return balance !== undefined ? `$${(balance / 100).toFixed(2)}` : '$0.00';
  }

  get isParticipantInactive() {
    return !this.args.model.tasProgramInstance?.employee?.tasParticipant?.isActive;
  }

  get annualLimit() {
    const annualLimit = this.limitsData?.details?.scheme?.annual_limit;

    if (annualLimit !== undefined && annualLimit !== null) {
      return `$${(annualLimit / 100).toFixed(2)}`;
    }

    return 'N/A';
  }

  get courseCount() {
    return this.args.model.activeCourses?.length || 0;
  }

  get useBalanceMultiplier() {
    return (
      this.args.model.tasProgramInstance?.tasProgramTemplate
        ?.useBalanceMultiplierForProgramLimits || false
    );
  }

  get allAttachments() {
    const programInstanceAssets = this.args.model.tasProgramInstance?.tasAssets || [];
    const courseAssets =
      this.args.model.activeCourses?.flatMap((course) => course.tasAssets || []) || [];
    const appAssets = this.args.model.tasAssets || [];
    const assistanceAssets =
      this.args.model.tasAssistances?.flatMap((assistance) => assistance.tasAssets || []) || [];

    // Deduplicate by ID since assets may be linked to multiple relationships
    const seenIds = new Set<string>();
    const allAssets = [
      ...programInstanceAssets,
      ...courseAssets,
      ...appAssets,
      ...assistanceAssets,
    ];
    return allAssets.filter((asset) => {
      const id = asset.id;
      if (!id || seenIds.has(id)) return false;
      seenIds.add(id);
      return true;
    });
  }

  get allAssetSummaries() {
    const summaries = this.allAttachments
      .map((asset) => asset.assetSummary)
      .filter((summary): summary is NonNullable<typeof summary> => summary != null);

    return summaries;
  }

  get timelineHistory() {
    return this.args.model.fullHistory || [];
  }

  get hasTimelineData() {
    return this.timelineHistory.length > 0;
  }

  get aiRecommendation() {
    const recommendations = this.args.model.aiRecommendations;
    if (!recommendations || recommendations.length === 0) return undefined;
    const sorted = [...recommendations].sort(
      (a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
    );
    return sorted[0];
  }

  get aiRecommendationStatus() {
    const responseJson = this.aiRecommendation?.responseJson as
      AiRecommendationResponse | undefined;
    const status = responseJson?.recommendation_summary?.status;
    if (!status) return null;

    const statusMap: Record<string, string> = {
      approved_for_requested_amount: 'Approve Application',
      approved_for_less_than_requested: 'Approve (Less Than Requested)',
      approved_for_more_than_requested: 'Approve (More Than Requested)',
      denied: 'Do Not Approve',
    };

    return statusMap[status] || status;
  }

  get aiCalculationDetails() {
    const responseJson = this.aiRecommendation?.responseJson as
      AiRecommendationResponse | undefined;
    const details = responseJson?.recommendation_summary?.calculation_details;
    if (details && typeof details === 'object') {
      return JSON.stringify(details, null, 2);
    }
    return details ? String(details) : null;
  }

  get hasAiRecommendation() {
    return !!this.aiRecommendation && !!this.aiRecommendationStatus;
  }

  /**
   * Returns a new custom-field array matched by label and province. Unmatched updates are ignored
   * because this flow edits existing fields only.
   */
  updateFieldsWithNewValues = (
    existingFields: CustomFieldSignature[],
    updatedFields: CustomFieldSignature[]
  ): CustomFieldSignature[] => {
    return mergeCustomFieldValues(existingFields, updatedFields);
  };

  get canEditCourse() {
    return !this.isEditingLocked && (this.sessionContext.canEditTasApplication ?? false);
  }

  get isReadonlyView() {
    return this.args.isReadonlyView === true;
  }

  /** Reserved Course Cash locks tuition because it must stay aligned with the card reservation. */
  get isCourseCashTuitionLocked() {
    const isCourseCash = this.args.model.paymentMethod === 'TAS.AssistanceManner.COURSE_CASH';
    const hasReservedFunds = (this.args.model.amounts?.course_cash?.reserved ?? 0) > 0;
    return isCourseCash && hasReservedFunds;
  }

  get isEditingCourseExpenses() {
    return this.editingCourseExpensesIndex === this.selectedCourseIndex;
  }

  get editableExpenseFields() {
    return ['COURSE_TUITION', 'COURSE_BOOKS', 'COURSE_LABS', 'COURSE_SOFTWARE', 'COURSE_FEES'];
  }

  /** Returns a course expense in dollars for currency input; stored values are cents. */
  getCourseExpenseValue = (fieldName: string): string => {
    if (!this.courseExpenseFieldsCopy) return '0';
    const value = getSingleValueForTasField(
      fieldName as keyof TasCourseModel['fields'],
      this.courseExpenseFieldsCopy
    );
    return value ? String(Number(value) / 100) : '0';
  };

  get expenseChangelogValidationErrors(): string[] {
    const schema = v.pipe(v.string(), v.minLength(1, 'Comment is required'));
    const result = v.safeParse(schema, this.expenseChangelog);
    return result.success ? [] : result.issues.map((issue) => issue.message);
  }

  get courseExpenseValidationErrors() {
    return Object.fromEntries(
      this.editableExpenseFields.map((fieldName) => {
        const value = getSingleValueForTasField(
          fieldName as keyof TasCourseModel['fields'],
          this.courseExpenseFieldsCopy ?? undefined
        );
        const amount = Number(value ?? 0);
        const errors =
          Number.isFinite(amount) && amount >= 0
            ? []
            : [this.intl.t('common.tas.approval.course_expense_nonnegative')];
        return [fieldName, errors] as const;
      })
    );
  }

  get isExpenseFormValid(): boolean {
    return (
      this.expenseChangelogValidationErrors.length === 0 &&
      Object.values(this.courseExpenseValidationErrors).every((errors) => errors.length === 0)
    );
  }

  toggleCourseDetailEdit = () => {
    const isCurrentlyEditing = this.editingCourseDetailsIndex === this.selectedCourseIndex;

    if (isCurrentlyEditing) {
      this.editingCourseDetailsIndex = null;
      this.courseDetailFieldsCopy = null;
      this.courseCustomFieldsCopy = null;
      this.detailChangelog = '';
    } else {
      const course = this.selectedCourse;
      if (!course) return;

      this.courseDetailFieldsCopy = trackedObject(course.fields);
      this.courseCustomFieldsCopy = course.customFields
        ? JSON.parse(JSON.stringify(course.customFields))
        : [];
      this.detailChangelog = '';
      this.editingCourseDetailsIndex = this.selectedCourseIndex;
    }
  };

  updateCourseDetailField = (fieldName: string, value: string | number) => {
    if (!this.courseDetailFieldsCopy) return;

    let valueToSet: string | number = value;

    if (fieldName === 'COURSE_CREDIT') {
      valueToSet = Math.round(Number(value));
    }

    setSingleValueForTasField(
      fieldName as keyof TasCourseModel['fields'],
      valueToSet,
      this.courseDetailFieldsCopy
    );
  };

  getCourseDetailValue = (fieldName: string): string => {
    if (!this.courseDetailFieldsCopy) return '';
    const value = getSingleValueForTasField(
      fieldName as keyof TasCourseModel['fields'],
      this.courseDetailFieldsCopy
    );
    return value ? String(value) : '';
  };

  updateDetailChangelog = (value: string) => {
    this.detailChangelog = value;
  };

  saveCourseDetails = dropTask(async () => {
    const course = this.selectedCourse;
    if (!course || !this.courseDetailFieldsCopy) {
      return;
    }

    if (!this.isDetailFormValid) {
      return;
    }

    try {
      course.fields = this.courseDetailFieldsCopy;
      if (this.courseCustomFieldsCopy) {
        course.customFields = this.courseCustomFieldsCopy;
      }

      await saveExistingRecordWithChangelog(this.store, course, this.detailChangelog);

      this.tuitionAssistance.refreshActiveLimitsSummaryComponents();

      this.editingCourseDetailsIndex = null;
      this.courseDetailFieldsCopy = null;
      this.courseCustomFieldsCopy = null;
      this.detailChangelog = '';

      this.courseDetailsFor(course);
    } catch (error) {
      console.error('Error saving course details:', error);
      // TODO: Show error notification to user
    }
  });

  get detailChangelogValidationErrors(): string[] {
    const schema = v.pipe(v.string(), v.minLength(1, 'Comment is required'));
    const result = v.safeParse(schema, this.detailChangelog);
    return result.success ? [] : result.issues.map((issue) => issue.message);
  }

  get isDetailFormValid(): boolean {
    if (this.detailChangelogValidationErrors.length > 0) {
      return false;
    }

    if (this.courseCustomFieldsCopy) {
      for (const field of this.courseCustomFieldsCopy) {
        const errors = this.getCourseCustomFieldValidationErrors(field.name || '');
        if (errors.length > 0) {
          return false;
        }
      }
    }

    return true;
  }

  /** Returns required-field validation messages for the named course custom field. */
  getCourseCustomFieldValidationErrors = (fieldName: string): string[] => {
    if (!this.courseCustomFieldsCopy) return [];

    const field = this.courseCustomFieldsCopy.find((f) => f.name === fieldName);
    if (!field) return [];

    const errors: string[] = [];

    if (field.required) {
      const hasValue =
        field.values &&
        field.values.length > 0 &&
        field.values[0] !== null &&
        field.values[0] !== undefined &&
        field.values[0] !== '';

      if (!hasValue) {
        errors.push(`${field.label || fieldName} is required`);
      }
    }

    // Future: Add more validation rules here (min/max length, patterns, etc.)

    return errors;
  };

  /**
   * Get form validation errors for course detail fields
   * Returns empty array for now as we don't have field-level validation yet
   */
  get courseDetailFormValidationErrors(): ValidationErrorSignature[] {
    return [];
  }

  get isEditingCourseDetails() {
    return this.editingCourseDetailsIndex === this.selectedCourseIndex;
  }

  get gradeOptions() {
    const course = this.selectedCourse;
    const programTemplate = this.args.model.tasProgramInstance?.tasProgramTemplate;

    if (!course || !programTemplate) return [];

    const templateFields = programTemplate.fields as
      Record<string, { value_as_field_option?: boolean }> | undefined;
    const gradeConfig = templateFields?.['COURSE_GRADE'];

    if (gradeConfig?.value_as_field_option) {
      return getSortedOptionsByFieldName('COURSE_GRADE', programTemplate.tasFieldOptions);
    }

    const customField = course.customFields?.find((f) => f.name === 'COURSE_GRADE');
    if (customField) {
      if (customField.value_as_field_option) {
        return getSortedOptionsByFieldName('COURSE_GRADE', programTemplate.tasFieldOptions);
      }
      return customField.options || [];
    }

    return [];
  }

  get editableCourseDetailFields() {
    return ['COURSE_NAME', 'COURSE_NUMBER', 'COURSE_DESCRIPTION', 'COURSE_CREDIT', 'COURSE_GRADE'];
  }

  /** Updates the tracked edit copy without changing the live course. */
  didUpdateCourseCustomFields = (customFields: CustomFieldSignature[]) => {
    if (!this.courseCustomFieldsCopy) return;

    const updatedFields = this.updateFieldsWithNewValues(this.courseCustomFieldsCopy, customFields);

    this.courseCustomFieldsCopy = updatedFields;
  };

  get courseCustomFieldValidationErrors(): ValidationErrorSignature[] {
    if (!this.courseCustomFieldsCopy) return [];

    return this.courseCustomFieldsCopy
      .filter((field) => field.province === 'COURSE')
      .map((field) => ({
        path: field.label || field.name || '',
        value: field.values?.[0]?.toString() || '',
        errors: this.getCourseCustomFieldValidationErrors(field.name || ''),
      }))
      .filter((error) => error.errors.length > 0);
  }

  /**
   * Returns COURSE custom fields from the tracked copy while editing, otherwise from the live
   * course.
   */
  courseCustomFieldsFor = (course: TasCourseModel): CustomFieldSignature[] => {
    if (
      this.isEditingCourseDetails &&
      course === this.selectedCourse &&
      this.courseCustomFieldsCopy
    ) {
      return this.courseCustomFieldsCopy
        .filter((field) => field.visible !== false)
        .sort((a, b) => (a.ordinal || 0) - (b.ordinal || 0));
    }

    if (!course?.customFields) return [];

    return course.customFields
      .filter((field) => field.visible !== false) // Show by default unless explicitly hidden
      .sort((a, b) => (a.ordinal || 0) - (b.ordinal || 0)) as CustomFieldSignature[];
  };

  get expenseType(): string {
    const expenseType = this.args.model.tasProgramInstance?.tasProgramTemplate?.expenseType;
    return expenseType || 'Courses';
  }

  getTabLabel = (index: number): string => {
    const appExpenseType = this.expenseType;
    return appExpenseType ? `${appExpenseType} ${index + 1}` : `Course ${index + 1}`;
  };

  /** Formats course expenses with labels from the program template. */
  courseExpensesFor = (course: TasCourseModel) => {
    if (!course) return [];

    const programTemplate = this.args.model.tasProgramInstance?.tasProgramTemplate;

    const getFieldLabel = (fieldName: string, fallback: string): string => {
      const fields = course.fields as Record<string, { values: unknown[] }> | undefined;
      if (fields?.[fieldName] && programTemplate) {
        const templateFields = programTemplate.fields as ProgramTemplateFields;
        const config = templateFields?.[fieldName];
        if (config?.label) return config.label;
      }

      const customField = course.customFields?.find((f) => f.name === fieldName);
      if (customField?.label) return customField.label;

      return fallback;
    };

    const expenses = [
      {
        label: getFieldLabel('COURSE_TUITION', 'Tuition!'),
        amount: course.courseTuition ? `$${(course.courseTuition / 100).toFixed(2)}` : '$0.00',
        isTotal: false,
      },
      {
        label: getFieldLabel('COURSE_BOOKS', 'Books'),
        amount: course.courseBooks ? `$${(course.courseBooks / 100).toFixed(2)}` : '$0.00',
        isTotal: false,
      },
      {
        label: getFieldLabel('COURSE_LABS', 'Labs'),
        amount: course.courseLabs ? `$${(course.courseLabs / 100).toFixed(2)}` : '$0.00',
        isTotal: false,
      },
      {
        label: getFieldLabel('COURSE_SOFTWARE', 'Software'),
        amount: course.courseSoftware ? `$${(course.courseSoftware / 100).toFixed(2)}` : '$0.00',
        isTotal: false,
      },
      {
        label: getFieldLabel('COURSE_FEES', 'Fees'),
        amount: course.courseFees ? `$${(course.courseFees / 100).toFixed(2)}` : '$0.00',
        isTotal: false,
      },
      {
        label: 'Total',
        amount: course.courseExpenses ? `$${(course.courseExpenses / 100).toFixed(2)}` : '$0.00',
        isTotal: true,
      },
    ];

    return expenses;
  };

  /** Formats standard and custom course details with labels from the program template. */
  courseDetailsFor = (course: TasCourseModel) => {
    if (!course) return [];

    const programTemplate = this.args.model.tasProgramInstance?.tasProgramTemplate;

    const getFieldLabel = (fieldName: string, fallback: string): string => {
      const fields = course.fields as Record<string, { values: unknown[] }> | undefined;
      if (fields?.[fieldName] && programTemplate) {
        const templateFields = programTemplate.fields as ProgramTemplateFields;
        const config = templateFields?.[fieldName];
        if (config?.label) return config.label;
      }

      const customField = course.customFields?.find((f) => f.name === fieldName);
      if (customField?.label) return customField.label;

      return fallback;
    };

    const getFieldValue = (fieldName: string): string | number | boolean | null => {
      const fields = course.fields as Record<string, { values: unknown[] }> | undefined;
      const value = fields?.[fieldName]?.values?.[0];
      if (value !== undefined) return value as string | number | boolean;

      const customField = course.customFields?.find((f) => f.name === fieldName);
      return customField?.values?.[0] || null;
    };

    const details = [];

    const courseName = course.courseName || getFieldValue('COURSE_NAME');
    if (courseName) {
      details.push({
        label: getFieldLabel('COURSE_NAME', 'Course Name'),
        value: String(courseName),
      });
    }

    const courseNumber = course.courseNumber || getFieldValue('COURSE_NUMBER');
    if (courseNumber) {
      details.push({
        label: getFieldLabel('COURSE_NUMBER', 'Course Number'),
        value: String(courseNumber),
      });
    }

    const courseDescription = course.courseDescription || getFieldValue('COURSE_DESCRIPTION');
    if (courseDescription) {
      details.push({
        label: getFieldLabel('COURSE_DESCRIPTION', 'Course Description'),
        value: String(courseDescription),
      });
    }

    const credits = course.courseCredit || getFieldValue('COURSE_CREDIT');
    if (credits) {
      details.push({
        label: getFieldLabel('COURSE_CREDIT', 'Credits'),
        value: String(credits),
      });
    }

    if (course.hasGrades) {
      const grade = course.courseGrade || getFieldValue('COURSE_GRADE');
      if (grade) {
        details.push({
          label: getFieldLabel('COURSE_GRADE', 'Grade'),
          value: String(grade),
        });
      }
    }

    const customFields = (course.customFields || [])
      .filter((field) => field.visible !== false) // Show by default unless explicitly hidden
      .sort((a, b) => (a.ordinal || 0) - (b.ordinal || 0));

    const context = { intl: this.intl, fieldOptions: programTemplate?.tasFieldOptions };
    customFields.forEach((field) => {
      const formattedValue = formatCustomFieldAnswer(field, context);
      if (formattedValue) {
        details.push({
          label: field.label || field.name || '',
          value: formattedValue,
        });
      }
    });

    return details;
  };

  <template>
    {{pageTitle (t "tuition_assistance.application_approval.title")}}

    <VStack
      class="min-h-screen {{if this.docksReferences 'tas-approval-layout'}}"
      data-reference-controls={{this.referenceControls}}
      data-reference-open={{if this.isReferenceOpen "true"}}
      ...attributes
    >
      <Section>
        <div class="flex-1 pb-32 tas-approval-content">
          <div class="max-w-7xl mx-auto">
            <VStack @collapsed={{true}}>
              <Section>
                <:header>
                  {{yield to="header"}}
                </:header>
              </Section>

              {{#if this.isReadonlyView}}
                <div class="mt-4 mb-2 px-4">
                  <AlertBanner
                    @intent="info"
                    @live={{false}}
                    @title={{t "common.tas_approval_queue.read_only_title"}}
                    aria-label={{t "common.tas_approval_queue.read_only_title"}}
                    data-test-approval-read-only-banner
                  >
                    {{t "common.tas_approval_queue.read_only_body"}}
                  </AlertBanner>
                </div>
              {{/if}}

              {{#if this.features.isAdminApp}}
                <div class="mt-4 mb-2 px-4">
                  <ApprovalDecisionBanner
                    class="max-w-3xl"
                    @eligibilityData={{this.eligibilityData}}
                    @limitsData={{this.limitsData}}
                    @limitsUnavailable={{this.isLimitsUnavailable}}
                    @isLoading={{this.eligibilityLoading}}
                    @periodsOfEligibility={{this.periodsOfEligibilityData.value}}
                    @isPostPaymentReview={{this.isPostPaymentReview}}
                    @heldFunds={{this.heldFundsSummary}}
                    @onOpenHeldFunds={{this.openHeldFunds}}
                  />
                </div>
              {{/if}}

              {{#if (has-block "alerts")}}
                {{yield to="alerts"}}
              {{/if}}

              <RecentCommentPreview @model={{@model}} @onViewMore={{this.openCommentsDrawer}} />

              <FinancialDetailsAdminSummary
                @application={{@model}}
                @limitsData={{this.limitsData}}
                @limitsUnavailable={{this.isLimitsUnavailable}}
              />

              <RepaymentWarningBanner @model={{@model}} />

              {{! AI Analysis Section -- Temporarily disabled. Re-enabling needs imports:
                  Button ('tio-ui/components/button'), PhSparkle/PhThumbsUp/PhThumbsDown
                  ('ember-phosphor-icons') }}
              {{!-- {{#if this.hasAiRecommendation}}
                <Section>
                  <:body>
                    <div
                      class="tio-ai-analysis-surface"
                    >
                      {{! Header }}
                      <div
                        class="tio-row-between px-6 py-4 border-b tio-border-default"
                      >
                        <div class="tio-row-snug">
                          <PhSparkle @size="16" />
                          <span class="tio-text-base-semibold tio-fg-primary">AI
                            Analysis</span>
                        </div>
                        <div class="tio-row-tight">
                          <Button
                            @appearance="minimal"
                            @intent="default"
                            @size="sm"
                            class="tio-fg-muted hover:tio-fg-secondary"
                            title="Approve"
                            data-test-quick-approve-button
                          >
                            <PhThumbsUp @size="20" />
                          </Button>
                          <Button
                            @appearance="minimal"
                            @intent="default"
                            @size="sm"
                            class="tio-fg-muted hover:tio-fg-secondary"
                            title="Reject"
                            data-test-quick-reject-button
                          >
                            <PhThumbsDown @size="20" />
                          </Button>
                        </div>
                      </div>
                      {{! Body }}
                      <div class="px-6 py-4 space-y-3">
                        <div class="tio-flex-tight">
                          <span
                            class="tio-text-sm-semibold tio-fg-primary"
                          >Recommendation:</span>
                          <span
                            class="tio-text-sm-normal tio-fg-secondary"
                          >{{this.aiRecommendationStatus}}</span>
                        </div>
                        {{#if this.aiCalculationDetails}}
                          <p class="text-sm tio-fg-secondary leading-relaxed">
                            {{this.aiCalculationDetails}}
                          </p>
                        {{/if}}
                      </div>
                    </div>
                  </:body>
                </Section>
              {{/if}} --}}

              <ApplicationDetailsSection
                @model={{@model}}
                @isReadOnly={{this.isEditingLocked}}
                @isEditingApplicationDetails={{this.isEditingApplicationDetails}}
                @onEditingApplicationDetailsChange={{this.setIsEditingApplicationDetails}}
                @applicationFieldsCopy={{this.applicationFieldsCopy}}
                @onApplicationFieldsCopyChange={{this.setApplicationFieldsCopy}}
                @onApplicationTotalChange={{this.setEditableApplicationTotal}}
                @programTemplateRoute={{this.programTemplateRoute}}
                @updateFieldsWithNewValues={{this.updateFieldsWithNewValues}}
              />

              <ExpensesSection
                @model={{@model}}
                @isReadOnly={{this.isEditingLocked}}
                @expenseType={{this.expenseType}}
                @onCourseOpen={{this.openCourseDrawer}}
                @onScholarshipOpen={{this.openScholarshipDrawer}}
              />
            </VStack>
          </div>
        </div>

        <ApprovalFooter
          @onDrawerOpen={{this.openDrawer}}
          @onReferenceToggle={{this.toggleReference}}
          @activeDrawer={{this.activeReference}}
          @approveButtonLoading={{false}}
          @application={{@model}}
          @isEditingApplication={{this.hasUnsavedEdits}}
          @ai={{@ai}}
          @expenseType={{this.expenseType}}
          @hasCourseCash={{this.hasCourseCash}}
          @nonModalDecisions={{this.docksReferences}}
          @onDecisionFormChange={{this.setDecisionFormOpen}}
          @referenceControls={{this.referenceControls}}
          @onMoveReferenceControls={{if this.docksReferences this.moveReferenceControls.perform}}
          {{this.observeActionBar}}
        />
      </Section>

      {{#if this.docksReferences}}
        <div class="contents" {{this.captureReferenceHost}}></div>
      {{/if}}
    </VStack>

    <Drawer
      @isOpen={{this.isReferenceOpen}}
      @onClose={{this.closeDrawer}}
      @size="xl"
      @placement="right"
      @nonModal={{this.docksReferences}}
      @destination={{this.referenceHost}}
      @manageHistory={{not this.docksReferences}}
      @ariaLabel={{t "common.tas.approval.reference_controls.drawer_label"}}
      @closeOnOutsideClick={{true}}
      @closeOnEscapeKey={{true}}
      @allowCloseButton={{true}}
      class={{if this.docksReferences "tas-approval-reference"}}
      data-test-drawer
    >
      {{#if @isCourseCashOpen}}
        {{yield to="courseCash"}}
      {{else if this.isAiAnalysis}}
        <AiAnalysisDrawer @application={{@model}} @recommendations={{@model.aiRecommendations}} />
      {{else if this.isComments}}
        <CommentsDrawer
          @model={{@model}}
          @authoredByPerson={{this.sessionContext.user.person}}
          @authoredByUser={{this.sessionContext.user}}
          @initialTab={{this.commentsInitialTab}}
        />
      {{else if this.isCourseDetails}}
        <CoursesDrawer
          @courses={{@model.activeCourses}}
          @selectedCourseIndex={{this.selectedCourseIndex}}
          @onCourseTabChange={{this.courseTabDidChange}}
          @courseExpensesFor={{this.courseExpensesFor}}
          @courseDetailsFor={{this.courseDetailsFor}}
          @courseCustomFieldsFor={{this.courseCustomFieldsFor}}
          @getTabLabel={{this.getTabLabel}}
          @programTemplate={{@model.tasProgramInstance.tasProgramTemplate}}
          @canEditCourse={{this.canEditCourse}}
          @isCourseTuitionLocked={{this.isCourseCashTuitionLocked}}
          @isEditingExpenses={{this.isEditingCourseExpenses}}
          @onToggleExpenseEdit={{this.toggleCourseExpenseEdit}}
          @onSaveExpenses={{this.saveCourseExpenses.perform}}
          @onUpdateExpense={{this.updateCourseExpenseField}}
          @getCourseExpenseValue={{this.getCourseExpenseValue}}
          @expenseChangelog={{this.expenseChangelog}}
          @onExpenseChangelogUpdate={{this.updateExpenseChangelog}}
          @expenseChangelogErrors={{this.expenseChangelogValidationErrors}}
          @expenseValidationErrors={{this.courseExpenseValidationErrors}}
          @isExpenseFormValid={{this.isExpenseFormValid}}
          @isExpensesSaving={{this.saveCourseExpenses.isRunning}}
          @isEditingDetails={{this.isEditingCourseDetails}}
          @onToggleDetailEdit={{this.toggleCourseDetailEdit}}
          @onSaveDetails={{this.saveCourseDetails.perform}}
          @onUpdateDetail={{this.updateCourseDetailField}}
          @onDidUpdateCourseCustomFields={{this.didUpdateCourseCustomFields}}
          @getCourseDetailValue={{this.getCourseDetailValue}}
          @courseCustomFieldValidationErrors={{this.courseCustomFieldValidationErrors}}
          @detailChangelog={{this.detailChangelog}}
          @onDetailChangelogUpdate={{this.updateDetailChangelog}}
          @detailChangelogErrors={{this.detailChangelogValidationErrors}}
          @isDetailFormValid={{this.isDetailFormValid}}
          @isDetailsSaving={{this.saveCourseDetails.isRunning}}
          @fieldsCopy={{this.courseDetailFieldsCopy}}
          @formValidationErrors={{this.courseDetailFormValidationErrors}}
        />
      {{else if this.isEmployerInfo}}
        <PartnerInfoDrawer
          @model={{@model}}
          @approverInstructionsItems={{this.approverInstructionsItems}}
          @employeeFaqsItems={{this.employeeFaqsItems}}
          @isAdminApp={{this.features.isAdminApp}}
        />
      {{else if this.isFinancialDetails}}
        <FinancialDetailsDrawer
          @application={{@model}}
          @programInstance={{@model.tasProgramInstance}}
          @editableApplicationTotal={{this.editableApplicationTotal}}
          @editableCourseCashTotal={{this.editableCourseCashTotal}}
          @isReadonlyView={{this.isEditingLocked}}
          @onUpdateApprovedTotal={{this.updateApprovedTotal}}
          @onSaveClaimsFinancials={{this.saveUpdatedClaimsFinancialsWithApprovedTotal}}
          @onUpdateClaimsFinancials={{this.didUpdateClaimsFinancials}}
          @onSetApplicationTotal={{this.setApplicationTotal}}
          @onSetCourseCashTotal={{this.setCourseCashTotal}}
          @onInvoiceDateChange={{this.updateInvoiceDate}}
        />
      {{else if this.isAttachments}}
        <AttachmentsDrawer
          @model={{@model}}
          @allAttachments={{this.allAttachments}}
          @classificationOptions={{classificationOptions}}
          @defaultClassification={{this.selectedClassification}}
          @programTemplate={{@model.tasProgramInstance.tasProgramTemplate}}
          @onUploadComplete={{this.hideUploadSection}}
          @hideDiscarded={{this.features.isEmployeeApp}}
          as |sections|
        >
          <sections.UploadSection
            @canUpload={{sections.uploadProps.canUpload}}
            @showUploadSection={{sections.uploadProps.showUploadSection}}
            @toggleUploadSection={{sections.uploadProps.toggleUploadSection}}
            @selectedClassification={{sections.uploadProps.selectedClassification}}
            @classificationDidChange={{sections.uploadProps.classificationDidChange}}
            @uploadFile={{sections.uploadProps.uploadFile}}
            @classificationOptions={{sections.uploadProps.classificationOptions}}
            @categoryOptions={{sections.uploadProps.categoryOptions}}
            @selectedCategory={{sections.uploadProps.selectedCategory}}
            @categoryDidChange={{sections.uploadProps.categoryDidChange}}
            @uploadDisabled={{sections.uploadProps.uploadDisabled}}
            @accept={{sections.uploadProps.accept}}
          />

          {{#if this.canGeneratePdfAsset}}
            <Button
              @intent="primary"
              @appearance="outlined"
              @icon={{PhFilePdf}}
              @label={{t
                "common.tas.approval.drawer.attachments.generate_pdf"
                default="Generate voucher PDF"
              }}
              @isRunning={{this.generatePdfAsset.isRunning}}
              class="w-full"
              {{on "click" this.openGeneratePdfConfirm}}
              data-test-generate-pdf-asset-button
            />

            <ConfirmationDialog
              @isOpen={{this.showGeneratePdfConfirm}}
              @title={{t
                "common.tas.approval.drawer.attachments.generate_pdf_confirm_title"
                default="Generate voucher PDF?"
              }}
              @intent={{if this.existingLifecycleDocumentsCount "warning" "info"}}
              @confirmText={{t
                "common.tas.approval.drawer.attachments.generate_pdf_confirm_action"
                default="Generate"
              }}
              @cancelText={{t "common.cancel" default="Cancel"}}
              @isRunning={{this.generatePdfAsset.isRunning}}
              @onConfirm={{this.generatePdfAsset.perform}}
              @onCancel={{this.closeGeneratePdfConfirm}}
              data-test-generate-pdf-confirm-dialog
            >
              <div class="tio-col-tight">
                {{#if this.existingLifecycleDocumentsCount}}
                  <AlertBanner @intent="warning" data-test-generate-pdf-existing-warning>
                    {{t
                      "common.tas.approval.drawer.attachments.generate_pdf_confirm_existing"
                      count=this.existingLifecycleDocumentsCount
                    }}
                  </AlertBanner>
                {{/if}}
                <p>
                  {{#if this.voucherDocumentName}}
                    {{t
                      "common.tas.approval.drawer.attachments.generate_pdf_confirm_body_named"
                      name=this.voucherDocumentName
                    }}
                  {{else}}
                    {{t
                      "common.tas.approval.drawer.attachments.generate_pdf_confirm_body"
                      default="This queues the voucher PDF for this application. It will appear with the other attachments once it finishes processing."
                    }}
                  {{/if}}
                </p>
              </div>
            </ConfirmationDialog>
          {{/if}}

          {{#if (eq this.consumingApp "tio-admin")}}
            <AttachmentsAnalysisView
              @attachments={{sections.attachments}}
              @assetSummaries={{sections.assetSummaries}}
              @deleteAttachment={{sections.deleteAttachment}}
              @isDeleted={{sections.isDeleted}}
              @isDevOps={{this.sessionContext.user.isTioDevOps}}
              @showOpenAll={{true}}
            />
          {{else}}
            <sections.TableView
              @sortedAttachments={{sections.tableProps.sortedAttachments}}
              @getFileIcon={{sections.tableProps.getFileIcon}}
              @getClassificationLabel={{sections.tableProps.getClassificationLabel}}
              @isDeleted={{sections.tableProps.isDeleted}}
              @deleteAttachment={{sections.tableProps.deleteAttachment}}
              @disabled={{sections.tableProps.disabled}}
              @showUploadSection={{sections.tableProps.showUploadSection}}
            />
          {{/if}}
        </AttachmentsDrawer>
      {{else if this.isTimeline}}
        <TimelineDrawer @history={{this.timelineHistory}} />
      {{else if this.isReassignApprover}}
        <ApprovalQueueStatusDrawer @application={{@model}} @consumingApp={{this.consumingApp}} />
      {{/if}}
    </Drawer>

    <AddScholarshipsDrawer
      @isOpen={{this.isScholarshipDrawerOpen}}
      @onClose={{this.closeScholarshipDrawer}}
      @application={{@model}}
    />

    {{#if this.heldFundsLoadedFor}}
      <HeldFundsModal
        @isOpen={{this.isHeldFundsOpen}}
        @onClose={{this.closeHeldFunds}}
        @applicationId={{this.heldFundsLoadedFor.applicationId}}
        @rows={{this.heldFundsRows}}
        @window={{this.heldFundsLoadedFor.window}}
        @limits={{this.limitsData}}
        @employeeName={{this.employeeName}}
        @companyName={{@model.tasProgramInstance.employee.company.name}}
        @isRefreshing={{this.awaitLedger.isRunning}}
        @onReduced={{this.onHeldFundsReduced}}
        @onStale={{this.reloadHeldFunds}}
      />
    {{/if}}
  </template>
}
